import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy
} from "@angular/core";
import { InsurerProgressStatus } from "src/app/share/constants/quotation-progress";
import { QuotationProgressService } from "../quotation-progress.service";

const SWIPE_THRESHOLD_PX = 24;
const TOOLTIP_GUTTER_PX = 16;
const SHEET_HEIGHT_VAR = "--qp-sheet-h";

// Mobile progress component: a collapsible footer sheet fixed to the bottom of the screen
@Component({
  selector: "app-quotation-progress-sheet",
  templateUrl: "./quotation-progress-sheet.component.html",
  styleUrls: ["./quotation-progress-sheet.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("expand", [
      state("false", style({ height: 0, opacity: 0 })),
      state("true", style({ height: "*", opacity: 1 })),
      transition("false <=> true", animate("300ms ease-in-out"))
    ])
  ]
})
export class QuotationProgressSheetComponent
  implements AfterViewInit, OnDestroy
{
  view$ = this.progress.view$;
  expanded = false;
  openTooltip: InsurerProgressStatus | null = null;
  // Horizontal nudge that keeps the tooltip inside the screen; the arrow moves the other way
  tooltipShift = 0;

  private touchStartY: number | null = null;
  private swallowNextClick = false;
  private resizeObserver: ResizeObserver;

  constructor(
    private progress: QuotationProgressService,
    private host: ElementRef<HTMLElement>,
    private cd: ChangeDetectorRef
  ) {}

  get showMascot(): boolean {
    return this.progress.showMascot;
  }

  ngAfterViewInit(): void {
    // Publish the sheet height so the page can reserve space for it and
    // scroll-to-card keeps cards above it
    this.resizeObserver = new ResizeObserver(() => {
      const height = this.host.nativeElement.getBoundingClientRect().height;
      this.progress.bottomInset = height;
      document.documentElement.style.setProperty(
        SHEET_HEIGHT_VAR,
        `${height}px`
      );
    });
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.progress.bottomInset = 0;
    document.documentElement.style.removeProperty(SHEET_HEIGHT_VAR);
  }

  toggleExpanded(): void {
    if (this.swallowNextClick) {
      this.swallowNextClick = false;
      return;
    }
    this.expanded = !this.expanded;
  }

  toggleTooltip(status: InsurerProgressStatus): void {
    this.openTooltip = this.openTooltip === status ? null : status;
    this.tooltipShift = 0;
    if (!this.openTooltip) return;

    // Render it centred over the chip first, then measure and pull it back inside the gutters
    this.cd.detectChanges();
    const tooltip =
      this.host.nativeElement.querySelector<HTMLElement>(".summary-tooltip");
    if (!tooltip) return;
    const rect = tooltip.getBoundingClientRect();
    const maxRight = document.documentElement.clientWidth - TOOLTIP_GUTTER_PX;
    if (rect.left < TOOLTIP_GUTTER_PX) {
      this.tooltipShift = TOOLTIP_GUTTER_PX - rect.left;
    } else if (rect.right > maxRight) {
      this.tooltipShift = maxRight - rect.right;
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartY = event.touches[0]?.clientY ?? null;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.touchStartY === null) return;
    const deltaY =
      (event.changedTouches[0]?.clientY ?? this.touchStartY) - this.touchStartY;
    this.touchStartY = null;

    // Short movement is a tap, which the click handler toggles
    if (Math.abs(deltaY) < SWIPE_THRESHOLD_PX) return;

    this.expanded = deltaY < 0;
    // Some browsers still fire a click after a swipe; don't let it toggle back
    this.swallowNextClick = true;
    setTimeout(() => (this.swallowNextClick = false), 400);
  }

  // Tapping outside the sheet collapses it; tapping anywhere but a chip closes the tooltip
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const insideSheet = this.host.nativeElement.contains(target);

    if (this.openTooltip && !target.closest?.(".summary-chip")) {
      this.openTooltip = null;
      this.cd.markForCheck();
    }
    if (!insideSheet && this.expanded) {
      this.expanded = false;
      this.cd.markForCheck();
    }
  }
}
